import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import type {PropsWithChildren} from "react";
import {
  beginAuthorization,
  exchangeAuthorizationCode,
  jwtClaims,
  logoutUrl,
  refreshTokens
} from "../api/oidc";
import type {OidcConfig, OidcTokens, OidcTransaction} from "../api/oidc";

const SESSION_KEY = "workbench.oidc.session";
const TRANSACTION_KEY = "workbench.oidc.transaction";
const RENEW_WINDOW_MS = 30_000;

export interface AuthenticatedUser {
  subject: string;
  username: string;
  displayName: string;
  tenant: string;
  company: string;
  businessUnit: string;
  roles: string[];
}

interface AuthContextValue {
  loading: boolean;
  user: AuthenticatedUser | null;
  login(): Promise<void>;
  logout(): void;
  authorizedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({children}: PropsWithChildren) {
  const [config, setConfig] = useState<OidcConfig | null>(null);
  const [session, setSession] = useState<OidcTokens | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionRef = useRef<OidcTokens | null>(null);
  const refreshPromise = useRef<Promise<OidcTokens> | null>(null);

  const store = useCallback((tokens: OidcTokens | null) => {
    if (tokens) actor(tokens.accessToken);
    sessionRef.current = tokens;
    setSession(tokens);
    if (tokens) sessionStorage.setItem(SESSION_KEY, JSON.stringify(tokens));
    else sessionStorage.removeItem(SESSION_KEY);
  }, []);

  useEffect(() => {
    let active = true;
    void fetch("/runtime-config.json", {headers: {Accept: "application/json"}})
      .then(async response => {
        if (!response.ok) throw new Error(`Runtime configuration failed: ${response.status}`);
        return await response.json() as OidcConfig;
      })
      .then(async runtimeConfig => {
        if (!active) return;
        setConfig(runtimeConfig);
        const callback = new URL(window.location.href);
        if (callback.pathname === new URL(runtimeConfig.redirectUri).pathname) {
          const transaction = parse<OidcTransaction>(
            sessionStorage.getItem(TRANSACTION_KEY));
          const state = callback.searchParams.get("state");
          const code = callback.searchParams.get("code");
          if (!transaction || !state || state !== transaction.state || !code) {
            throw new Error("OIDC callback state validation failed");
          }
          const exchanged = await exchangeAuthorizationCode(
            runtimeConfig, code, transaction.verifier);
          sessionStorage.removeItem(TRANSACTION_KEY);
          store(exchanged);
          window.history.replaceState({}, "", transaction.returnTo);
          window.dispatchEvent(new PopStateEvent("popstate"));
        } else {
          const saved = parse<OidcTokens>(sessionStorage.getItem(SESSION_KEY));
          if (saved && saved.refreshExpiresAt > Date.now()) {
            if (saved.expiresAt <= Date.now() + RENEW_WINDOW_MS) {
              store(await refreshTokens(runtimeConfig, saved.refreshToken));
            } else {
              store(saved);
            }
          } else {
            store(null);
          }
        }
      })
      .catch(() => store(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [store]);

  const renew = useCallback(async (): Promise<OidcTokens> => {
    if (!config) throw new Error("OIDC configuration is unavailable");
    const current = sessionRef.current;
    if (!current || current.refreshExpiresAt <= Date.now()) {
      store(null);
      throw new Error("OIDC session expired");
    }
    if (!refreshPromise.current) {
      refreshPromise.current = refreshTokens(config, current.refreshToken)
        .then(tokens => {
          store(tokens);
          return tokens;
        })
        .catch(error => {
          store(null);
          throw error;
        })
        .finally(() => {
          refreshPromise.current = null;
        });
    }
    return await refreshPromise.current;
  }, [config, store]);

  const login = useCallback(async () => {
    if (!config) throw new Error("OIDC configuration is unavailable");
    const result = await beginAuthorization(
      config, `${window.location.pathname}${window.location.search}`);
    sessionStorage.setItem(TRANSACTION_KEY, JSON.stringify(result.transaction));
    window.location.assign(result.authorizationUrl);
  }, [config]);

  const logout = useCallback(() => {
    const current = sessionRef.current;
    store(null);
    if (config && current) window.location.assign(logoutUrl(config, current.idToken));
  }, [config, store]);

  useEffect(() => {
    if (!session) return;
    const wait = Math.max(0, session.expiresAt - Date.now() - RENEW_WINDOW_MS);
    const timer = window.setTimeout(() => {
      void renew().catch(() => void login());
    }, wait);
    return () => window.clearTimeout(timer);
  }, [session, renew, login]);

  const authorizedFetch = useCallback(async (
    input: RequestInfo | URL,
    init: RequestInit = {}
  ): Promise<Response> => {
    let current = sessionRef.current;
    if (!current) {
      await login();
      throw new Error("Authentication redirect started");
    }
    if (current.expiresAt <= Date.now() + RENEW_WINDOW_MS) current = await renew();
    const request = (accessToken: string) => fetch(input, {
      ...init,
      headers: {
        ...Object.fromEntries(new Headers(init.headers).entries()),
        Authorization: `Bearer ${accessToken}`
      }
    });
    let response = await request(current.accessToken);
    if (response.status === 401) {
      try {
        current = await renew();
        response = await request(current.accessToken);
      } catch {
        await login();
        throw new Error("Authentication redirect started");
      }
      if (response.status === 401) {
        store(null);
        await login();
        throw new Error("Authentication redirect started");
      }
    }
    return response;
  }, [login, renew, store]);

  const user = useMemo(
    () => session ? actor(session.accessToken) : null,
    [session]
  );
  const value = useMemo<AuthContextValue>(
    () => ({loading, user, login, logout, authorizedFetch}),
    [loading, user, login, logout, authorizedFetch]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is required");
  return value;
}

function actor(accessToken: string): AuthenticatedUser {
  const claims = jwtClaims(accessToken);
  const realmAccess = claims.realm_access as {roles?: unknown} | undefined;
  const roles = Array.isArray(realmAccess?.roles)
    ? realmAccess.roles.filter((role): role is string => typeof role === "string")
    : [];
  return {
    subject: required(claims, "sub"),
    username: required(claims, "preferred_username"),
    displayName: required(claims, "name"),
    tenant: required(claims, "tenant"),
    company: required(claims, "company"),
    businessUnit: required(claims, "business_unit"),
    roles
  };
}

function required(claims: Record<string, unknown>, claim: string): string {
  const value = claims[claim];
  if (typeof value !== "string" || !value) {
    throw new Error(`JWT ${claim} claim is required`);
  }
  return value;
}

function parse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}
